import React from 'react';
import { Paper, Typography, Box } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Student } from '@/types';

interface GradeBreakdownProps {
  students: Student[];
}

export const GradeBreakdown: React.FC<GradeBreakdownProps> = ({ students }) => {
  const gradeData = Array.from({ length: 4 }, (_, i) => {
    const grade = i + 9; // Grades 9-12
    const gradeStudents = students.filter(s => s.grade === grade);
    const totalStudents = gradeStudents.length;
    
    // Handle case where there are no students in a grade
    if (totalStudents === 0) {
      return {
        grade: `Grade ${grade}`,
        averageAttendance: 0,
        chronicAbsences: 0,
        totalStudents: 0
      };
    }

    const averageAttendance = gradeStudents.reduce((sum, s) => sum + s.attendanceRate, 0) / totalStudents;
    const chronicAbsences = gradeStudents.filter(s => s.attendanceRate < 90).length;
    
    return {
      grade: `Grade ${grade}`,
      averageAttendance: Number(averageAttendance.toFixed(1)) || 0,
      chronicAbsences,
      totalStudents
    };
  });

  return (
    <Paper sx={{ p: 3, height: '400px' }}>
      <Typography variant="h6" gutterBottom>
        Grade-Level Attendance Breakdown
      </Typography>

      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={gradeData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="grade" />
          <YAxis 
            yAxisId="left" 
            orientation="left" 
            stroke="#8884d8"
            domain={[0, 100]}
            label={{ 
              value: 'Attendance %', 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle' }
            }}
          />
          <YAxis 
            yAxisId="right" 
            orientation="right" 
            stroke="#82ca9d"
            label={{ 
              value: 'Students', 
              angle: 90, 
              position: 'insideRight',
              style: { textAnchor: 'middle' }
            }}
          />
          <Tooltip 
            formatter={(value: number) => value.toFixed(1)}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
          <Legend />
          <Bar
            yAxisId="left"
            dataKey="averageAttendance"
            name="Average Attendance (%)"
            fill="#8884d8"
            radius={[4, 4, 0, 0]}
          />
          <Bar
            yAxisId="right"
            dataKey="chronicAbsences"
            name="Chronic Absences"
            fill="#82ca9d"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
};